import * as React from 'react';
import Form from 'antd/lib/form';
import withStyles from 'react-jss';
import Navbar from '../navbar/Navbar';
import UnauthorizedAccess from '../common/UnauthorizedAccess';
import withAuth from './withAuth';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { HocOptions, PermissionTypes } from './GenericHoc.types';
import { MasterData } from 'src/Library/MasterDataType';
import { OrgTypes , ModesOfService , UserRole } from 'src/Library/Constants';
import { equal } from 'src/Library/Helper';
import withInquiryMasterData from '../pages/Enquiry/hoc/InitInquiryMaster';
import { ORGANISATION_SIZE } from '../pages/Enquiry/constants';

const GenericHoc = (options: HocOptions) => {
  const {
    connectRedux,
    connectForm,
    connectRouter,
    connectJss,
    permissionOptions,
    useInquiryMasterData,
    ignoreWithAuth
  } = options;

  function reduxConnector(Component) {
    if (!connectRedux?.useRedux) {
      return Component;
    }
    const { mapStateToProps, mapDispatchToProps } = connectRedux;
    return connect(mapStateToProps, mapDispatchToProps)(Component);
  }

  function inquiryMasterDataConnector(Component) {
    if (!useInquiryMasterData) {
      return Component;
    }
    return withInquiryMasterData(Component);
  }

  function formConnector(Component) {
    if (!connectForm?.useForm) {
      return Component;
    }
    const { formOptions } = connectForm;
    return Form.create(formOptions || {})(Component);
  }

  function jssConnector(Component) {
    if (!connectJss?.useJss) {
      return Component;
    }
    const { styleSheet } = connectJss;
    return withStyles(styleSheet)(Component);
  }

  function routeConnector(Component) {
    if (!connectRouter) {
      return Component;
    }
    return withRouter(Component);
  }

  function isMyOrganisationFromMasters(masterData: MasterData, match) {
    const { userData } = masterData;
    const { type } = userData;
    return (
      match?.params?.orgType &&
      match?.params?.orgId
    );
  }

  function checkNonNegotiableAccess(
    permissionKey: UserRole | string,
    masterData: MasterData,
    authUser: Record<string, boolean>
  ): boolean {
    const { partsToShow } = authUser;
    const { userData, permissions } = masterData;
    switch (permissionKey) {
      case UserRole.ADMIN:
      case UserRole.MEMBER:
        return userData.role === UserRole[permissionKey] || permissions.USER_MANAGEMENT_WRITE;
      case PermissionTypes.USER_MANAGEMENT_WRITE:
        return userData.role === UserRole.ADMIN || permissions[permissionKey];
      case OrgTypes.FF:
      case OrgTypes.CHA:
      case OrgTypes.SUPPLIER:
      case OrgTypes.CUSTOMER:
      case OrgTypes.SHIPPING_LINE:
      case OrgTypes.SHIPPER:
        return userData.type === OrgTypes[permissionKey];
      case PermissionTypes.CONTAINER_TRACKING_WRITE:
      case PermissionTypes.INQUIRY_ALL_ACTIONS:
      case PermissionTypes.INQUIRY_CREATE_UPDATE_ACTIONS:
      case PermissionTypes.SHIPMENT_ADD_EDIT:
        return permissions[permissionKey];
      case PermissionTypes.CT_NEW_SHIPMENT_VIEW:
        return masterData.newShipmentView;
      case PermissionTypes.ORGANISATION_SIZE_NOT_SME:
        return userData.organisationSize !== ORGANISATION_SIZE.SME;
      default:
        return partsToShow[permissionKey];
    }
  }

  function checkPermissions(masterData: MasterData, authUser: Record<string, boolean>): boolean {
    const { partsToShow } = authUser;
    if (!permissionOptions) {
      return true;
    }
    const { negotiable, nonNegotiable } = permissionOptions;
    let hasPermission = true;
    nonNegotiable?.forEach((permissionKey: string) => {
      const hasCurrentPermission = checkNonNegotiableAccess(permissionKey, masterData, authUser);
      hasPermission = hasPermission && hasCurrentPermission;
    });
    if (!negotiable?.length) {
      return hasPermission;
    }
    const negotiablePermission = negotiable.some((permissionKey) => partsToShow[permissionKey]);
    return hasPermission && negotiablePermission;
  }

  return (Component) => {
    Component = reduxConnector(Component);
    Component = formConnector(Component);
    Component = routeConnector(Component);
    Component = jssConnector(Component);
    Component = inquiryMasterDataConnector(Component);
    class ComponentClass extends React.PureComponent<any, any> {
      render() {
        const { masterData, match, authUser } = this.props;
        const hasPermission = checkPermissions(masterData, authUser);
        const isCurrentOrgFromMasters = isMyOrganisationFromMasters(masterData, match);
        if (!hasPermission && !isCurrentOrgFromMasters) {
          return (
            <div>
              <Navbar pageTitle="Not Authorized" />
              <UnauthorizedAccess />
            </div>
          );
        }
        return <Component {...this.props} />;
      }
    }
    const state = ({ masterData, authUser }) => {
      return {
        masterData,
        authUser,
      };
    };
    const AuthenticatedComponent = withAuth(ComponentClass);
    return connect(state)(ignoreWithAuth ? ComponentClass : AuthenticatedComponent);
  };
};

export default GenericHoc;
